import React, { useState } from 'react';
import { Save, Trash2, Copy, Eye, EyeOff, Check } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сохраненных паролей
 */
const SavedPasswordsSidebar = ({ 
  savedPasswords, 
  deleteSavedPassword,
  setShowPassword
}) => {
  const [showPasswords, setShowPasswords] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  
  // Если нет сохраненных паролей
  if (!savedPasswords || savedPasswords.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <Save className="w-5 h-5 mr-2 text-violet-500" />
          Сохраненные пароли
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные пароли для быстрого доступа.
        </p>
        
        <div className="mt-4 bg-violet-50 dark:bg-violet-900/20 border border-violet-100 dark:border-violet-800 rounded-lg p-3">
          <p className="text-xs text-violet-600 dark:text-violet-400">
            Сохраняйте важные пароли для повторного использования. Пароли хранятся только в вашем браузере и никуда не передаются.
          </p>
        </div>
      </div>
    );
  }
  
  // Копирование пароля в буфер обмена
  const copyToClipboard = (password, id) => {
    navigator.clipboard.writeText(password).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      alert('Не удалось скопировать пароль. Попробуйте выделить его вручную.');
    });
  };
  
  // Переключение видимости пароля
  const togglePasswordVisibility = (id) => {
    setShowPasswords(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  // Форматирование даты создания
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  // Получение цвета для индикатора силы пароля
  const getStrengthColor = (strength) => {
    const colors = {
      0: 'bg-red-500',
      1: 'bg-red-500',
      2: 'bg-yellow-500',
      3: 'bg-green-500',
      4: 'bg-emerald-500'
    };
    return colors[strength] || 'bg-gray-300';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Save className="w-5 h-5 mr-2 text-violet-500" />
          Сохраненные пароли
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedPasswords.length}
        </span>
      </div>
      
      <div className="space-y-3">
        {savedPasswords.map((item) => (
          <div 
            key={item.id} 
            className="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-2 ${getStrengthColor(item.strength)}`}></div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  {item.name}
                </h4>
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => togglePasswordVisibility(item.id)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                  aria-label={showPasswords[item.id] ? "Скрыть пароль" : "Показать пароль"}
                >
                  {showPasswords[item.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => copyToClipboard(item.password, item.id)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                  aria-label="Копировать пароль"
                >
                  {copiedId === item.id ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => deleteSavedPassword(item.id)}
                  className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 p-1"
                  aria-label="Удалить пароль"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="font-mono text-sm break-all mb-1">
              {showPasswords[item.id] ? (
                <span className="text-gray-900 dark:text-white">{item.password}</span>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">••••••••••••</span>
              )}
            </div>
            
            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-500 dark:text-gray-400">
                {formatDate(item.date)}
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                {item.type === 'random' ? 'Случайный' : 
                 item.type === 'memorable' ? 'Запоминаемый' :
                 item.type === 'pin' ? 'PIN-код' : 'Фраза'}
              </span>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
        <p>
          Пароли хранятся только в вашем браузере и не отправляются на сервер. Очистка данных браузера может привести к потере сохраненных паролей.
        </p>
      </div>
    </div>
  );
};

export default SavedPasswordsSidebar;